/**
 * CanvasView — whiteboard runtime for a collab post.
 *
 * Composition root: the mechanics live in src/canvas/* modules —
 *   mergeBlocks        commit_id → one card per logical write-event
 *   layouts            masonry + timeline packing (pure functions)
 *   useStableCardKeys  React keys that survive the block_id lifecycle
 *   useCanvasStage     ViewportTransform + render loop + event routing
 *   cardInteractions   drag / resize local overrides
 *   CanvasCard         presentational card (markdown, chrome, resize)
 * REST side-effects (create / delete / image-drop) in api/canvasCrdt.
 *
 * Architecture: Canvas2D paints ONLY the Bezier edges; the cards are
 * DOM nodes in a world-space overlay driven by the same viewport
 * (Heptabase/Excalidraw split). The Doc↔Canvas morph lerps card
 * geometry via phase factors from useDocCanvasMorph.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  LayoutEngine,
  FreeCanvasLayouter,
  EdgeRenderer,
  EdgeStatePool,
  CardFrameStatePool,
  textBlockId,
} from '@arkturian/canvas-engine'
import type { TextBlock } from '@arkturian/canvas-engine'
import { useEdges, useEdgesInvalidateOnCommit } from '../hooks/useEdges'
import { useDomOverlay } from '../hooks/useDomOverlay'
import {
  useFieldMap,
  selectTensionParagraphIndices,
} from '../hooks/useFieldMap'
import type { MorphState } from '../hooks/useDocCanvasMorph'
import { mergeBlocksByCommit } from '../canvas/mergeBlocks'
import type { CanvasBlock, MergedBlock } from '../canvas/mergeBlocks'
import {
  computeMasonryLayout,
  computeTimelineLayout,
  buildTimelineTicks,
  CARD_W,
  CARD_H_MIN,
} from '../canvas/layouts'
import type { TimelineAxis } from '../canvas/layouts'
import { useStableCardKeys } from '../canvas/useStableCardKeys'
import { useCanvasStage } from '../canvas/useCanvasStage'
import type { CardOverrides } from '../canvas/cardInteractions'
import { CanvasCard } from '../canvas/CanvasCard'
import {
  createCardAt,
  deleteParagraphs,
  insertImageAfter,
} from '../api/canvasCrdt'

interface Props {
  postId: number
  blocks: TextBlock[]
  /** Latest doc version from chunk_committed; drives edges-cache invalidation. */
  docVersion?: number
  /** Doc↔Canvas morph state; absent → steady canvas (all phases = 1). */
  morph?: MorphState
  /** Card "Edit" → parent positions the CollabEditor popover; null closes. */
  onCardEdit?: (info: { blockId: string; paragraphIndex: number } | null) => void
  /** Block currently edited in the popover. */
  editingBlockId?: string | null
}

// Doc-mode geometry the morph lerps FROM (left-aligned linear stack).
const DOC_CARD_WIDTH = 720
const DOC_LINE_HEIGHT = 32

export function CanvasView({
  postId,
  blocks: rawBlocks,
  docVersion,
  morph,
  onCardEdit,
  editingBlockId,
}: Props) {
  const phase1 = morph?.phase1 ?? 1
  const phase2 = morph?.phase2 ?? 1
  const phase3 = morph?.phase3 ?? 1
  const phase4 = morph?.phase4 ?? 1

  // --- Commit grouping: one card per logical write-event -----------------
  const mergedBlocks = useMemo(
    () => mergeBlocksByCommit(rawBlocks as CanvasBlock[]),
    [rawBlocks],
  )
  // Virtual blocks the layout/render pipeline operates on: the primary
  // paragraph of each group, char_count summed across the group.
  const blocks = useMemo<CanvasBlock[]>(
    () =>
      mergedBlocks.map((m) => ({
        ...m.primary,
        char_count: m.combined_char_count,
      })),
    [mergedBlocks],
  )
  const mergedById = useMemo(() => {
    const map = new Map<string, MergedBlock>()
    for (const mb of mergedBlocks) {
      map.set(textBlockId(mb.primary), mb)
    }
    return map
  }, [mergedBlocks])

  // --- Engine + pools (one instance per mount) ---------------------------
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const [engine] = useState(
    () => new LayoutEngine<TextBlock>(new FreeCanvasLayouter()),
  )
  const [edgeStatePool] = useState(() => new EdgeStatePool())
  const [cardStatePool] = useState(() => new CardFrameStatePool())
  // Edge styles bumped vs. engine defaults: at typical zoom the default
  // 1.5px lines render sub-pixel and vanish against the card overlay.
  const [edgeRenderer] = useState(
    () =>
      new EdgeRenderer({
        styles: {
          replies_to: { stroke: 'rgba(70, 110, 200, 0.95)', lineWidth: 4, arrowSize: 14 },
          contradicts: { stroke: 'rgba(220, 80, 80, 0.95)', lineWidth: 4, lineDash: [10, 6], arrowSize: 14 },
          extends: { stroke: 'rgba(100, 120, 130, 0.85)', lineWidth: 4, arrowSize: 12 },
          references: { stroke: 'rgba(140, 140, 140, 0.75)', lineWidth: 2.5, lineDash: [3, 5] },
          synthesizes: { stroke: 'rgba(170, 100, 220, 0.95)', lineWidth: 5, arrowSize: 16 },
        },
      }),
  )

  const { data: edges = [] } = useEdges({ postId, enabled: true })
  useEdgesInvalidateOnCommit(postId, docVersion)

  const { viewport, stageSize, screenToWorld, zoomToOverview, zoomTo100 } =
    useCanvasStage(canvasRef, stageRef, engine, edgeRenderer, edgeStatePool, edges)
  const { overlayRef } = useDomOverlay(viewport)

  // --- View state --------------------------------------------------------
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [layoutMode, setLayoutMode] = useState<'masonry' | 'timeline'>('masonry')
  const editingId = editingBlockId ?? null
  const [localOverrides, setLocalOverrides] = useState<CardOverrides>({})

  const stableKeys = useStableCardKeys(blocks)

  // Real card heights reported by each card's ResizeObserver. The
  // char-count estimate badly overshoots for markdown-heavy content
  // (syntax counts, doesn't render) which left phantom gaps below
  // short cards; measured heights collapse those gaps on next layout.
  const [measuredHeights, setMeasuredHeights] = useState<Record<string, number>>({})
  const handleMeasure = useCallback((id: string, h: number) => {
    setMeasuredHeights((prev) =>
      Math.abs((prev[id] ?? 0) - h) < 2 ? prev : { ...prev, [id]: h },
    )
  }, [])

  // --- Layout ------------------------------------------------------------
  // Baseline positions are frozen per blocks-array (drag overrides are
  // separate state), so dragging one card never reshuffles the rest.
  const layout = useMemo<{
    rects: Record<string, { x: number; y: number; width: number; height: number }>
    axis: TimelineAxis | null
  }>(() => {
    if (layoutMode === 'timeline') {
      return computeTimelineLayout(blocks, stageSize.w, measuredHeights)
    }
    return { rects: computeMasonryLayout(blocks, measuredHeights), axis: null }
  }, [blocks, layoutMode, stageSize.w, measuredHeights])

  // Axis ticks derive from the SAME geometry the layout used — no
  // second mapping that can drift out of sync with the cards.
  const timelineTicks = useMemo(
    () => (layoutMode === 'timeline' ? buildTimelineTicks(layout.axis) : []),
    [layoutMode, layout.axis],
  )

  const effectiveBlocks = useMemo<CanvasBlock[]>(
    () =>
      blocks.map((b) => {
        const id = textBlockId(b)
        const base = layout.rects[id]
        const local = localOverrides[id]
        return {
          ...b,
          canvas_meta: {
            x: local?.x ?? base?.x ?? 0,
            y: local?.y ?? base?.y ?? 0,
            width: local?.width ?? base?.width ?? CARD_W,
            height: local?.height ?? base?.height ?? CARD_H_MIN,
          },
        }
      }),
    [blocks, layout.rects, localOverrides],
  )

  // --- Engine/pool sync --------------------------------------------------
  useEffect(() => {
    engine.sync(effectiveBlocks, textBlockId)
  }, [effectiveBlocks, engine])

  useEffect(() => {
    cardStatePool.gc(new Set(effectiveBlocks.map(textBlockId)))
  }, [effectiveBlocks, cardStatePool])

  useEffect(() => {
    edgeStatePool.sync(edges, (e) => e.edge_id, 1)
  }, [edges, edgeStatePool])

  useEffect(() => {
    for (const s of cardStatePool.all()) {
      s.setSelected(s.id === selectedId)
    }
  }, [selectedId, cardStatePool])

  // Ambient Counter-Salience: tension-flagged cards breathe via the
  // pool's sine pulse — atmosphere, not a UI element.
  const { data: fieldMap } = useFieldMap({ postId, enabled: true })
  const tensionIndices = useMemo(
    () => selectTensionParagraphIndices(fieldMap),
    [fieldMap],
  )
  useEffect(() => {
    for (const s of cardStatePool.all()) s.setFocusPulse(false)
    for (const b of effectiveBlocks) {
      if (tensionIndices.has(b.paragraph_index)) {
        cardStatePool.get(textBlockId(b)).setFocusPulse(true, 0.75)
      }
    }
  }, [tensionIndices, effectiveBlocks, cardStatePool])

  // Edge opacity per morph Phase 4: staggered fade-in on opening
  // (high-confidence first), joint dissolve with phase1 on closing.
  useEffect(() => {
    for (const e of edges) {
      const state = edgeStatePool.get(e.edge_id)
      if (!state) continue
      const conf = (e as { confidence?: number }).confidence ?? 0.85
      const target =
        morph?.direction === 'closing'
          ? 1 - phase1
          : conf >= 0.85
            ? phase4
            : Math.max(0, phase4 - 0.25) / 0.75
      state.opacity.targetValue = Math.max(0, Math.min(1, target))
    }
  }, [edges, edgeStatePool, phase1, phase4, morph?.direction])

  // Mode-switch INTO timeline resets the viewport to scale=1/origin so
  // the axis span is immediately visible at readable card size. Tracked
  // in a ref — the flag is never rendered, so no state/re-render needed.
  const timelineResetDoneRef = useRef(false)
  useEffect(() => {
    if (!viewport) return
    if (layoutMode !== 'timeline') {
      timelineResetDoneRef.current = false
      return
    }
    if (timelineResetDoneRef.current) return
    viewport.setImmediate(1, 0, 0)
    timelineResetDoneRef.current = true
  }, [viewport, layoutMode])

  // --- Card actions ------------------------------------------------------
  const handleCreateAt = useCallback(
    (worldX: number, worldY: number) => {
      void createCardAt(postId, worldX, worldY)
    },
    [postId],
  )

  // Deleting a merged card removes EVERY paragraph of its commit —
  // since the commit_id grouping, the card IS the commit.
  const handleDelete = useCallback(
    (merged: MergedBlock | undefined, block: CanvasBlock, cardId: string) => {
      const indices = merged
        ? merged.paragraphs.map((p) => p.paragraph_index)
        : [block.paragraph_index]
      void deleteParagraphs(postId, indices)
      if (selectedId === cardId) setSelectedId(null)
      if (editingId === cardId) onCardEdit?.(null)
      setLocalOverrides((prev) => {
        if (!(cardId in prev)) return prev
        const next = { ...prev }
        delete next[cardId]
        return next
      })
    },
    [postId, selectedId, editingId, onCardEdit],
  )

  const handleEditToggle = useCallback(
    (block: CanvasBlock, id: string, isEditing: boolean) => {
      if (isEditing) {
        onCardEdit?.(null)
      } else {
        onCardEdit?.({
          blockId: block.block_id ?? id,
          paragraphIndex: block.paragraph_index,
        })
        setSelectedId(id)
      }
    },
    [onCardEdit],
  )

  const handleImageDrop = useCallback(
    (block: CanvasBlock, file: File) => {
      insertImageAfter(
        postId,
        block.paragraph_index,
        block.block_id,
        file,
      ).catch((err) => {
        console.error('Image drop upload failed:', err)
      })
    },
    [postId],
  )

  // Double-click on empty background → create card there.
  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Element | null
      if (target?.closest('.canvas-card')) return
      if (target?.closest('.canvas-add-btn')) return
      const world = screenToWorld(e.clientX, e.clientY)
      handleCreateAt(world.x, world.y)
    }
    stage.addEventListener('dblclick', handler)
    return () => stage.removeEventListener('dblclick', handler)
  }, [screenToWorld, handleCreateAt])

  const addCardAtCenter = useCallback(() => {
    const stage = stageRef.current
    if (!stage) return
    const rect = stage.getBoundingClientRect()
    const world = screenToWorld(
      rect.left + rect.width / 2,
      rect.top + rect.height / 2,
    )
    handleCreateAt(world.x - 140, world.y - 60)
  }, [screenToWorld, handleCreateAt])

  // Delete/Backspace on the selected card (unless typing somewhere).
  useEffect(() => {
    if (!selectedId || editingId) return
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      const target = e.target as HTMLElement | null
      if (target) {
        if (target.isContentEditable) return
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      }
      const block = effectiveBlocks.find((b) => textBlockId(b) === selectedId)
      if (!block) return
      e.preventDefault()
      handleDelete(mergedById.get(selectedId), block, selectedId)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [selectedId, editingId, effectiveBlocks, mergedById, handleDelete])

  // --- Render ------------------------------------------------------------
  return (
    <div
      ref={stageRef}
      className="canvas-stage"
      data-morph-direction={morph?.direction ?? 'idle'}
    >
      <canvas ref={canvasRef} className="canvas-stage__canvas" />
      <div className="canvas-layout-toggle" role="group" aria-label="Layout-Modus">
        <button
          type="button"
          className={`canvas-layout-btn${layoutMode === 'masonry' ? ' is-active' : ''}`}
          title="Spalten-Layout (manuell + auto-stagger)"
          onClick={() => setLayoutMode('masonry')}
        >
          Masonry
        </button>
        <button
          type="button"
          className={`canvas-layout-btn${layoutMode === 'timeline' ? ' is-active' : ''}`}
          title="Timeline-Layout — Zeitachse über created_at, Greedy First-Fit Lanes"
          onClick={() => setLayoutMode('timeline')}
        >
          Timeline
        </button>
      </div>
      <div className="canvas-zoom-toolbar" role="group" aria-label="Zoom-Steuerung">
        <button
          type="button"
          className="canvas-zoom-btn"
          title="Übersicht — alle Karten ins Sichtfeld zoomen"
          onClick={() => zoomToOverview(Object.values(layout.rects))}
        >
          Übersicht
        </button>
        <button
          type="button"
          className="canvas-zoom-btn"
          title="Zoom 100% — zurück zur normalen Lesegröße"
          onClick={zoomTo100}
        >
          100%
        </button>
      </div>
      <button
        type="button"
        className="canvas-add-btn"
        title="Neue Kachel (oder Doppelklick auf Hintergrund)"
        onClick={addCardAtCenter}
      >
        +
      </button>
      <div ref={overlayRef} className="canvas-stage__overlay">
        {timelineTicks.length > 0 && (
          <div className="canvas-timeline-axis">
            {timelineTicks.map((t) => (
              <div
                key={t.x}
                className="canvas-timeline-tick"
                style={{ left: t.x }}
              >
                {t.label}
              </div>
            ))}
            {(layout.axis?.gaps ?? []).map((g) => (
              <div
                key={g.x}
                className="canvas-timeline-gap"
                style={{ left: g.x - g.width / 2, width: g.width }}
                title={`Zusammengefaltete Pause: ${g.label}`}
              >
                <span className="canvas-timeline-gap__label">{g.label}</span>
              </div>
            ))}
          </div>
        )}
        {effectiveBlocks.map((b) => {
          const id = textBlockId(b)
          const cm = b.canvas_meta
          if (!cm) return null
          const local = localOverrides[id]

          // Morph lerp: Phase 2 collapses doc-width/height toward the
          // card dims, Phase 3 travels doc-position → canvas-position.
          const docY = b.paragraph_index * DOC_LINE_HEIGHT * 1.6
          const docHeight = Math.max(
            56,
            DOC_LINE_HEIGHT * Math.ceil(b.char_count / 60),
          )
          const width = DOC_CARD_WIDTH + (cm.width - DOC_CARD_WIDTH) * phase2
          const height = docHeight + (cm.height - docHeight) * phase2
          const x = 24 + (cm.x - 24) * phase3
          const y = docY + (cm.y - docY) * phase3

          return (
            <CanvasCard
              key={stableKeys.get(id) ?? id}
              block={b}
              merged={mergedById.get(id)}
              id={id}
              x={x}
              y={y}
              width={width}
              height={phase2 < 0.999 ? height : undefined}
              localHeight={local?.height}
              opacity={0.4 + 0.6 * Math.max(phase1, phase2)}
              suppressTransition={Boolean(local) || (Boolean(morph) && phase3 < 0.999)}
              selected={selectedId === id}
              editing={editingId === id}
              tension={tensionIndices.has(b.paragraph_index)}
              onSelect={setSelectedId}
              onEditToggle={handleEditToggle}
              onDelete={handleDelete}
              onImageDrop={handleImageDrop}
              setOverrides={setLocalOverrides}
              onMeasure={handleMeasure}
            />
          )
        })}
      </div>
    </div>
  )
}
